import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SrIcon } from "@/components/ui/SrIcon";
import { formatNOK, formatDateShort } from "@/lib/format";
import { totalToPay } from "@/lib/extras-payment";
import { useExtrasAwaitingPayment, useSetExtrasPaymentState } from "@/hooks/use-extras-payment";
import { useRBAC } from "@/hooks/use-rbac";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { downloadCsv, extrasPaymentQueueToCsv } from "@/lib/report-export";

/**
 * VIB-2092: utbetalingslisten daglig leder henter under Rapporter.
 *
 * Erik 29.09: prosjektleder melder klar på prosjektkortet, DL henter lista her.
 * Derfor er dette en arbeidsliste med handling, ikke en periodisert rapport —
 * en statist står her til hen er betalt, uavhengig av hvilken periode som er
 * valgt over. Den lever som egen seksjon utenfor metrikk-rammeverket, samme
 * plassering som statistlønn-eksporten (VIB-1283).
 *
 * Dobbeltgatet som lønnseksporten: `isAdmin` I TILLEGG TIL flagget. Lista viser
 * personnummer og kontonummer, og skal aldri kunne bli synlig for alle roller
 * bare fordi flagget senere skrus til «all».
 */
export default function ExtrasPaymentQueue() {
  const { isAdmin } = useRBAC();
  const flagOn = useFeatureFlag("feature_extras_payment_flow");
  const visible = isAdmin && flagOn;

  const { data: rows = [], isLoading } = useExtrasAwaitingPayment(visible);
  const setPaymentState = useSetExtrasPaymentState();
  const [selected, setSelected] = useState<Set<string>>(new Set());

  if (!visible) return null;

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  const selectedRows = rows.filter((r) => selected.has(r.id));

  const handleMarkPaid = () => {
    if (selectedRows.length === 0) return;
    setPaymentState.mutate(
      {
        extraIds: selectedRows.map((r) => r.id),
        state: "paid",
        namesById: Object.fromEntries(selectedRows.map((r) => [r.id, r.full_name])),
      },
      { onSuccess: () => setSelected(new Set()) },
    );
  };

  const handleExport = () => {
    // Eksporter utvalget hvis noe er valgt, ellers hele lista — DL laster ofte
    // ned alt, men skal kunne ta en delmengde når bare noen skal betales nå.
    const toExport = selectedRows.length > 0 ? selectedRows : rows;
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`utbetaling-statister-${today}.csv`, extrasPaymentQueueToCsv(toExport));
  };

  return (
    <div className="rounded-lg border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div>
          <p className="text-sm font-medium">Statister klare for utbetaling</p>
          <p className="text-xs text-muted-foreground">
            Meldt klar av prosjektleder. Marker som utbetalt når pengene er sendt.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={handleExport}
            disabled={isLoading || rows.length === 0}
          >
            <SrIcon name="download" className="mr-1.5 h-3.5 w-3.5" />
            {selectedRows.length > 0 ? `Eksporter ${selectedRows.length}` : "Eksporter CSV"}
          </Button>
          <Button
            size="sm"
            onClick={handleMarkPaid}
            disabled={selectedRows.length === 0 || setPaymentState.isPending}
          >
            Marker utbetalt ({selectedRows.length})
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="p-4">
          <div className="h-20 animate-pulse rounded bg-muted" />
        </div>
      ) : rows.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-muted-foreground">
          Ingenting venter på utbetaling nå.
        </p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="w-10 px-4 py-2">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleAll}
                      aria-label="Velg alle"
                    />
                  </th>
                  <th className="px-2 py-2 text-left font-medium">Navn</th>
                  <th className="px-2 py-2 text-left font-medium">Prosjekt</th>
                  <th className="px-2 py-2 text-left font-medium">Prosjektleder</th>
                  <th className="px-2 py-2 text-left font-medium">Meldt klar</th>
                  <th className="px-2 py-2 text-right font-medium">Beløp</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-4 py-2">
                      <Checkbox
                        checked={selected.has(r.id)}
                        onCheckedChange={() => toggle(r.id)}
                        aria-label={`Velg ${r.full_name}`}
                      />
                    </td>
                    <td className="px-2 py-2">
                      <span className="font-medium">{r.full_name}</span>
                      {r.role && (
                        <span className="ml-1.5 text-xs text-muted-foreground">{r.role}</span>
                      )}
                      {/*
                        Manglende kontonummer skal ikke kunne nå hit — PL blir
                        stoppet av paymentBlockers ved melding. Vises likevel om
                        det skjer, så DL ikke oppdager det først i lønnssystemet.
                      */}
                      {!r.bank_account && (
                        <span className="ml-1.5 text-xs text-destructive">
                          mangler kontonr
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {r.project_id ? (
                        <Link
                          to={`/prosjekter/${r.project_id}`}
                          className="text-primary hover:underline"
                        >
                          {r.project_code ? `${r.project_code} ` : ""}
                          {r.project_name}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">{r.project_name}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">
                      {r.production_lead_name ?? "—"}
                    </td>
                    <td className="px-2 py-2 text-muted-foreground">
                      {r.ready_for_payment_at ? formatDateShort(r.ready_for_payment_at) : "—"}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums">
                      {formatNOK(r.agreed_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-muted/30 font-medium">
                  <td colSpan={5} className="px-4 py-2 text-right text-xs">
                    {selectedRows.length > 0 ? `Valgt (${selectedRows.length})` : "Totalt"}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {formatNOK(totalToPay(selectedRows.length > 0 ? selectedRows : rows))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
